"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useId, useState, type FormEvent } from "react";
import { Clock, CornerDownLeft, Gavel, Gem, Images, LayoutGrid, Rocket, Users } from "lucide-react";
import { MIN_QUERY, SEARCH_KINDS, SEARCH_TYPES, type SearchKind, type SearchType } from "@/features/search/v2/kinds";
import { toHits } from "@/features/search/v2/hits";
import { usePanelSearch } from "@/features/search/v2/usePanelSearch";
import { SearchField } from "./browse/BrowseControls";
import { Segmented, Spinner } from "./ui";
import { canAutoFocusField } from "@/lib/touch-focus";
import "./search.css";

/**
 * THE SEARCH PANEL.
 *
 * It drops out of the top bar rather than opening a centred dialog: the field
 * stays exactly where it was, and the results appear under it. That keeps the
 * visitor's spatial context, which a full-screen takeover destroys.
 *
 * It searches the same seven kinds as /search, over the same sources
 * (`usePanelSearch`), previews a few of each, and hands off to /search —
 * with the chosen kind — on submit.
 */

type Copy = {
  fa: string;
  en: string;
};
const LABEL: Record<SearchType, Copy> = {
  all: {
    fa: "همه",
    en: "All"
  },
  user: {
    fa: "هنرمندان",
    en: "Artists"
  },
  collection: {
    fa: "کالکشن",
    en: "Collections"
  },
  nft: {
    fa: "آثار",
    en: "Artwork"
  },
  drops: {
    fa: "دراپ",
    en: "Drops"
  },
  auctions: {
    fa: "حراج",
    en: "Auctions"
  },
  galleries: {
    fa: "گالری",
    en: "Galleries"
  },
  blogs: {
    fa: "مقاله",
    en: "Articles"
  }
};
const GROUP: Record<SearchKind, Copy> = {
  user: {
    fa: "هنرمندان",
    en: "Artists"
  },
  collection: {
    fa: "کالکشن‌ها",
    en: "Collections"
  },
  nft: {
    fa: "آثار",
    en: "Artwork"
  },
  drops: {
    fa: "دراپ‌ها",
    en: "Drops"
  },
  auctions: {
    fa: "حراج‌ها",
    en: "Auctions"
  },
  galleries: {
    fa: "گالری‌ها",
    en: "Galleries"
  },
  blogs: {
    fa: "مقاله‌ها",
    en: "Articles"
  }
};
const SHORTCUTS: {
  href: string;
  fa: string;
  en: string;
  Icon: typeof Gem;
}[] = [{
  href: "/market",
  fa: "همه‌ی آثار",
  en: "All artwork",
  Icon: LayoutGrid
}, {
  href: "/collection",
  fa: "کالکشن‌ها",
  en: "Collections",
  Icon: Gem
}, {
  href: "/artists",
  fa: "هنرمندان",
  en: "Artists",
  Icon: Users
}, {
  href: "/drops",
  fa: "دراپ‌ها",
  en: "Drops",
  Icon: Rocket
}, {
  href: "/auctions",
  fa: "حراج‌ها",
  en: "Auctions",
  Icon: Gavel
}, {
  href: "/galleries",
  fa: "گالری‌ها",
  en: "Galleries",
  Icon: Images
}];
/** In "all", three of each kind keep seven kinds inside one panel; one kind shows all four it asked for. */
const ALL_PER_KIND = 3;
export default function SearchPanel(t0) {
  const $ = _c(124);
  const {
    lang,
    open,
    onClose
  } = t0;
  const isFa = lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = c => isFa ? c.fa : c.en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  let t2;
  if ($[2] !== lang) {
    t2 = p => `/${lang}${p}`;
    $[2] = lang;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const href = t2;
  const inputId = useId();
  const [q, setQ] = useState("");
  const [entity, setEntity] = useState("all");
  const {
    results,
    pending,
    active
  } = usePanelSearch(q, open, lang);
  let t3;
  let t4;
  if ($[4] !== inputId || $[5] !== open) {
    t3 = () => {
      if (open && canAutoFocusField()) {
        document.getElementById(inputId)?.focus();
      }
    };
    t4 = [open, inputId];
    $[4] = inputId;
    $[5] = open;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[8] !== onClose || $[9] !== open) {
    t5 = () => {
      if (!open) {
        return;
      }
      const onKey = e => {
        if (e.key === "Escape") {
          onClose();
        }
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    };
    t6 = [open, onClose];
    $[8] = onClose;
    $[9] = open;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  if (!open) {
    return null;
  }
  let submit;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t7;
  let t8;
  let t9;
  if ($[12] !== active || $[13] !== entity || $[14] !== href || $[15] !== inputId || $[16] !== isFa || $[17] !== lang || $[18] !== onClose || $[19] !== pending || $[20] !== q || $[21] !== results || $[22] !== t) {
    const kinds = entity === "all" ? SEARCH_KINDS : [entity];
    const groups = results ? kinds.map(kind => ({
      kind,
      hits: toHits(kind, results, lang).slice(0, entity === "all" ? ALL_PER_KIND : undefined)
    })).filter(_temp) : [];
    const busy = active && kinds.some(k => pending.includes(k));
    const empty = active && !busy && groups.length === 0;
    let t18;
    if ($[35] !== entity || $[36] !== href || $[37] !== q) {
      t18 = e_0 => {
        e_0?.preventDefault();
        const text = q.trim();
        if (text.length < MIN_QUERY) {
          return;
        }
        window.location.href = href(`/search?q=${encodeURIComponent(text)}${entity === "all" ? "" : `&type=${entity}`}`);
      };
      $[35] = entity;
      $[36] = href;
      $[37] = q;
      $[38] = t18;
    } else {
      t18 = $[38];
    }
    submit = t18;
    let t19;
    if ($[39] !== t) {
      t19 = t({
        fa: "\u0628\u0633\u062A\u0646",
        en: "Close"
      });
      $[39] = t;
      $[40] = t19;
    } else {
      t19 = $[40];
    }
    if ($[41] !== onClose || $[42] !== t19) {
      t17 = <button className="v2-search-scrim" type="button" aria-label={t19} onClick={onClose} />;
      $[41] = onClose;
      $[42] = t19;
      $[43] = t17;
    } else {
      t17 = $[43];
    }
    t11 = "v2-search-panel";
    t12 = "dialog";
    t13 = "true";
    if ($[44] !== t) {
      t14 = t({
        fa: "\u062C\u0633\u062A\u200C\u0648\u062C\u0648",
        en: "Search"
      });
      $[44] = t;
      $[45] = t14;
    } else {
      t14 = $[45];
    }
    let t20;
    if ($[46] !== t) {
      t20 = t({
        fa: "\u062C\u0633\u062A\u200C\u0648\u062C\u0648",
        en: "Search"
      });
      $[46] = t;
      $[47] = t20;
    } else {
      t20 = $[47];
    }
    let t21;
    if ($[48] !== t) {
      t21 = t({
        fa: "\u067E\u0627\u06A9 \u06A9\u0631\u062F\u0646",
        en: "Clear"
      });
      $[48] = t;
      $[49] = t21;
    } else {
      t21 = $[49];
    }
    let t22;
    if ($[50] !== t) {
      t22 = t({
        fa: "\u0647\u0646\u0631\u0645\u0646\u062F\u060C \u06A9\u0627\u0644\u06A9\u0634\u0646\u060C \u0627\u062B\u0631\u060C \u062F\u0631\u0627\u067E\u060C \u062D\u0631\u0627\u062C\u060C \u06AF\u0627\u0644\u0631\u06CC \u06CC\u0627 \u0645\u0642\u0627\u0644\u0647\u2026",
        en: "Artist, collection, artwork, drop, auction, gallery\u2026"
      });
      $[50] = t;
      $[51] = t22;
    } else {
      t22 = $[51];
    }
    let t23;
    if ($[52] !== inputId || $[53] !== q || $[54] !== t20 || $[55] !== t21 || $[56] !== t22) {
      t23 = <SearchField id={inputId} value={q} onChange={setQ} label={t20} clearLabel={t21} placeholder={t22} />;
      $[52] = inputId;
      $[53] = q;
      $[54] = t20;
      $[55] = t21;
      $[56] = t22;
      $[57] = t23;
    } else {
      t23 = $[57];
    }
    const t24 = busy ? <Spinner /> : null;
    let t25;
    if ($[58] !== t) {
      t25 = t({
        fa: "\u0628\u0633\u062A\u0646",
        en: "Close"
      });
      $[58] = t;
      $[59] = t25;
    } else {
      t25 = $[59];
    }
    let t26;
    if ($[60] !== onClose || $[61] !== t25) {
      t26 = <button type="button" className="v2-search-close v2-focus" onClick={onClose}>{t25}</button>;
      $[60] = onClose;
      $[61] = t25;
      $[62] = t26;
    } else {
      t26 = $[62];
    }
    if ($[63] !== submit || $[64] !== t23 || $[65] !== t24 || $[66] !== t26) {
      t15 = <form className="v2-search-bar" role="search" onSubmit={submit}>{t23}{t24}{t26}</form>;
      $[63] = submit;
      $[64] = t23;
      $[65] = t24;
      $[66] = t26;
      $[67] = t15;
    } else {
      t15 = $[67];
    }
    let t27;
    if ($[68] !== t) {
      t27 = t({
        fa: "\u0646\u0648\u0639 \u0646\u062A\u06CC\u062C\u0647",
        en: "Result type"
      });
      $[68] = t;
      $[69] = t27;
    } else {
      t27 = $[69];
    }
    let t28;
    if ($[70] !== t) {
      t28 = SEARCH_TYPES.map(value => ({
        value,
        label: t(LABEL[value])
      }));
      $[70] = t;
      $[71] = t28;
    } else {
      t28 = $[71];
    }
    if ($[72] !== entity || $[73] !== t27 || $[74] !== t28) {
      t16 = <div className="v2-search-filters"><Segmented value={entity} onChange={setEntity} label={t27} options={t28} /></div>;
      $[72] = entity;
      $[73] = t27;
      $[74] = t28;
      $[75] = t16;
    } else {
      t16 = $[75];
    }
    t7 = "v2-search-body";
    if ($[76] !== active || $[77] !== href || $[78] !== isFa || $[79] !== onClose || $[80] !== t) {
      t8 = !active ? <div className="v2-search-group"><h3><Clock aria-hidden={true} />{t({
            fa: "\u0634\u0631\u0648\u0639 \u0627\u0632 \u0627\u06CC\u0646\u200C\u062C\u0627",
            en: "Start here"
          })}</h3>{SHORTCUTS.map(t29 => {
          const {
            href: p_0,
            fa,
            en,
            Icon
          } = t29;
          return <Link className="v2-search-row" key={p_0} href={href(p_0)} onClick={onClose}><span className="v2-search-ico"><Icon aria-hidden={true} /></span><span className="v2-search-name">{isFa ? fa : en}</span></Link>;
        })}</div> : null;
      $[76] = active;
      $[77] = href;
      $[78] = isFa;
      $[79] = onClose;
      $[80] = t;
      $[81] = t8;
    } else {
      t8 = $[81];
    }
    if ($[82] !== empty || $[83] !== t) {
      t9 = empty ? <p className="v2-search-empty">{t({
          fa: "\u0686\u06CC\u0632\u06CC \u067E\u06CC\u062F\u0627 \u0646\u0634\u062F. \u0627\u0645\u0644\u0627\u06CC \u062F\u06CC\u06AF\u0631\u06CC \u0631\u0627 \u0627\u0645\u062A\u062D\u0627\u0646 \u06A9\u0646\u06CC\u062F.",
          en: "Nothing found. Try another spelling."
        })}</p> : null;
      $[82] = empty;
      $[83] = t;
      $[84] = t9;
    } else {
      t9 = $[84];
    }
    let t29;
    if ($[85] !== onClose || $[86] !== t) {
      t29 = t30 => {
        const {
          kind: kind_0,
          hits
        } = t30;
        return <div className="v2-search-group" key={kind_0}><h3>{t(GROUP[kind_0])}</h3>{hits.map(h => <Link className="v2-search-row" key={h.key} href={h.href} onClick={onClose}><Image className={`v2-search-thumb${h.round ? " v2-search-thumb--round" : ""}`} src={h.image || "/sampleNft1.png"} alt="" width={34} height={34} /><span className="v2-search-name">{h.title}</span>{h.sub ? <span className={`v2-sub${h.ltrSub ? " v2-sub--ltr" : ""}`} dir={h.ltrSub ? "ltr" : "auto"}>{h.sub}</span> : null}</Link>)}</div>;
      };
      $[85] = onClose;
      $[86] = t;
      $[87] = t29;
    } else {
      t29 = $[87];
    }
    t10 = groups.map(t29);
    $[12] = active;
    $[13] = entity;
    $[14] = href;
    $[15] = inputId;
    $[16] = isFa;
    $[17] = lang;
    $[18] = onClose;
    $[19] = pending;
    $[20] = q;
    $[21] = results;
    $[22] = t;
    $[23] = submit;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
    $[30] = t16;
    $[31] = t17;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    submit = $[23];
    t10 = $[24];
    t11 = $[25];
    t12 = $[26];
    t13 = $[27];
    t14 = $[28];
    t15 = $[29];
    t16 = $[30];
    t17 = $[31];
    t7 = $[32];
    t8 = $[33];
    t9 = $[34];
  }
  let t18;
  if ($[88] !== t10 || $[89] !== t7 || $[90] !== t8 || $[91] !== t9) {
    t18 = <div className={t7}>{t8}{t9}{t10}</div>;
    $[88] = t10;
    $[89] = t7;
    $[90] = t8;
    $[91] = t9;
    $[92] = t18;
  } else {
    t18 = $[92];
  }
  let t19;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <kbd>Esc</kbd>;
    $[93] = t19;
  } else {
    t19 = $[93];
  }
  let t20;
  if ($[94] !== t) {
    t20 = t({
      fa: "\u0628\u0633\u062A\u0646",
      en: "to close"
    });
    $[94] = t;
    $[95] = t20;
  } else {
    t20 = $[95];
  }
  let t21;
  if ($[96] !== t20) {
    t21 = <span>{t19} {t20}</span>;
    $[96] = t20;
    $[97] = t21;
  } else {
    t21 = $[97];
  }
  let t22;
  if ($[98] !== submit) {
    t22 = () => submit();
    $[98] = submit;
    $[99] = t22;
  } else {
    t22 = $[99];
  }
  let t23;
  if ($[100] !== q) {
    t23 = q.trim();
    $[100] = q;
    $[101] = t23;
  } else {
    t23 = $[101];
  }
  const t24 = t23.length < MIN_QUERY;
  let t25;
  if ($[102] !== t) {
    t25 = t({
      fa: "\u062F\u06CC\u062F\u0646 \u0647\u0645\u0647\u200C\u06CC \u0646\u062A\u0627\u06CC\u062C",
      en: "See all results"
    });
    $[102] = t;
    $[103] = t25;
  } else {
    t25 = $[103];
  }
  let t26;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <CornerDownLeft aria-hidden={true} />;
    $[104] = t26;
  } else {
    t26 = $[104];
  }
  let t27;
  if ($[105] !== t22 || $[106] !== t24 || $[107] !== t25) {
    t27 = <button type="button" className="v2-search-go" onClick={t22} disabled={t24}>{t25}{t26}</button>;
    $[105] = t22;
    $[106] = t24;
    $[107] = t25;
    $[108] = t27;
  } else {
    t27 = $[108];
  }
  let t28;
  if ($[109] !== t21 || $[110] !== t27) {
    t28 = <div className="v2-search-foot">{t21}{t27}</div>;
    $[109] = t21;
    $[110] = t27;
    $[111] = t28;
  } else {
    t28 = $[111];
  }
  let t29;
  if ($[112] !== t11 || $[113] !== t12 || $[114] !== t13 || $[115] !== t14 || $[116] !== t15 || $[117] !== t16 || $[118] !== t18 || $[119] !== t28) {
    t29 = <div className={t11} role={t12} aria-modal={t13} aria-label={t14}>{t15}{t16}{t18}{t28}</div>;
    $[112] = t11;
    $[113] = t12;
    $[114] = t13;
    $[115] = t14;
    $[116] = t15;
    $[117] = t16;
    $[118] = t18;
    $[119] = t28;
    $[120] = t29;
  } else {
    t29 = $[120];
  }
  let t30;
  if ($[121] !== t17 || $[122] !== t29) {
    t30 = <>{t17}{t29}</>;
    $[121] = t17;
    $[122] = t29;
    $[123] = t30;
  } else {
    t30 = $[123];
  }
  return t30;
}
function _temp(g) {
  return g.hits.length > 0;
}